import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleCheckIcon,
  CircleMinusIcon,
  CircleXIcon,
  DocumentIcon,
  LoaderIcon,
} from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import RegistryRecord from 'dashboard/components/ReportResult/BusinessRegistrationsLookup/RegistryRecord';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { formatDate as fd } from 'dashboard/lib/utils/formatters';
import { formatCompanyPosition, nameCase } from 'dashboard/lib/utils/stringFormat';
import { StandardizedStatus, UsSecretaryOfStateRecord } from 'dashboard/models/registry-record';
import {
  RegistryRecordEnrichment,
  RegistryRecordEnrichmentStatus,
  ReportBusinessRegistrationsLookup,
} from 'dashboard/models/report/business-registrations-lookup';
import Attribute from 'design-system/components/Attribute';
import AttributeGrid from 'design-system/components/Attribute/AttributeGrid';
import Card from 'design-system/components/Card';
import Select, { Option } from 'design-system/components/Select';
import { Heading, Paragraph } from 'design-system/components/Text';
import { rotate } from 'design-system/variables/keyframes';
import { US_STATES, US_TERRITORIES } from 'lib/constants/locales';
import { UsSecretaryOfState } from 'lib/constants/usSecretaryOfStates';
import { assertNever } from 'lib/utils/types';

const US_STATES_AND_TERRITORIES = {
  ...US_STATES,
  ...US_TERRITORIES,
};

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 300px auto;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;

// Note: 168px is the sum of the report heading height + all the top and bottom paddings in the report view
// this is the value needed to make the page not scroll.
const SideNav = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[3]} ${Spacing[4]};
  height: calc(100vh - 168px);
  grid-row: span 2;
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SideNavFixedSizeContainer = styled.div`
  flex: 0 0 auto;
`;

const SideNavFillSizeContainer = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
`;

// Note: 168px is the sum of the report heading height + all the top and bottom paddings in the report view
// this is the value needed to make the page not scroll.
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 168px);
  overflow: auto;
  padding: ${Spacing[5]} ${Spacing[5]} 0px ${Spacing[5]};
  grid-row: 2;
  grid-column: 2;
`;

const StyledAttributeGrid = styled(AttributeGrid)`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  padding-bottom: ${Spacing[3]};
  padding-left: ${Spacing[3]};
`;

const StyledParagraph = styled(Paragraph)`
  padding-left: ${Spacing[3]};
`;

const SearchFilterBarContainer = styled.div`
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 4fr 1fr 1fr;
  grid-gap: ${Spacing[3]};
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
`;

const RegistryRecordsContainer = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
`;

const StyledLi = styled.li<{
  selected: boolean;
}>`
  padding: ${Spacing[2]} ${Spacing[3]};
  margin-right: ${Spacing[3]};
  margin-top: ${Spacing[1]};
  border-radius: ${BorderRadius.Md};
  display: flex;
  justify-content: space-between;

  ${({ selected }) =>
    selected &&
    `
    background-color: ${Color.CorePurple100};
    color: ${Color.CorePurple700};
  `}

  :hover {
    background-color: ${Color.CorePurple100};
    color: ${Color.CorePurple700};
    cursor: pointer;
  }
`;

const IconContainer = styled.div`
  display: grid;
  place-items: center;
`;

const LoaderSpinner = styled(LoaderIcon)`
  height: 16px;
  width: 16px;
  color: ${Color.BorderIconLabelGrey};
  animation: ${rotate} linear 3s infinite;
`;

const StatusIcon = ({ status }: { status: StandardizedStatus }) => {
  switch (status) {
    case 'active':
      return <CircleCheckIcon size='16px' color={Color.BorderIconSuccess} />;
    case 'inactive':
      return <CircleXIcon size='16px' color={Color.BorderIconDanger} />;
    case 'unknown':
      return <CircleMinusIcon size='16px' color={Color.BorderIconLabelGrey} />;
    default:
      assertNever(status);
  }
};

const DocumentStatusIcon = ({
  enrichmentStatus,
  registryRecord,
}: {
  enrichmentStatus: RegistryRecordEnrichmentStatus;
  registryRecord: UsSecretaryOfStateRecord;
}) => {
  switch (enrichmentStatus) {
    case 'pending':
      return (
        <IconContainer>
          <LoaderSpinner aria-label='Document pending icon' />
        </IconContainer>
      );
    case 'completed':
      if (registryRecord.documents && registryRecord.documents.length > 0) {
        return (
          <IconContainer>
            <DocumentIcon
              aria-label='Document icon'
              size='16px'
              color={Color.BorderIconLabelGrey}
            />
          </IconContainer>
        );
      } else {
        return <></>;
      }
    case 'errored':
      return <></>;
    default:
      return <></>;
  }
};

const StatePicker = ({
  records,
  enrichmentsMapping,
  selectedView,
  setSelectedView,
}: {
  records: UsSecretaryOfStateRecord[];
  enrichmentsMapping: Record<string, RegistryRecordEnrichment>;
  selectedView: 'all' | UsSecretaryOfStateRecord;
  setSelectedView: Dispatch<SetStateAction<'all' | UsSecretaryOfStateRecord>>;
}) => {
  return (
    <SideNavFillSizeContainer>
      <StyledParagraph size='lg' weight='bold' color='primary' as='h4'>
        Secretary of State Filings
      </StyledParagraph>
      <ul>
        <StyledLi
          role='button'
          selected={selectedView === 'all'}
          id='state-picker-option-all'
          onClick={() => setSelectedView('all')}
        >
          All
        </StyledLi>
        {records
          .sort((a, b) => compareRegistryRecords(a, b, 'status'))
          .filter((record) => !!record['registration-state'])
          .map((record) => {
            const registrationState = record['registration-state'] as string;
            return (
              <StyledLi
                role='button'
                selected={selectedView === record}
                key={`state-picker-option__${registrationState}-${record['file-number']}}`}
                id={`state-picker-option__${registrationState}-${record['file-number']}}`}
                onClick={() => setSelectedView(record)}
              >
                {record['registration-type'] === 'Domestic' ? (
                  <div>
                    {registrationState}
                    <Paragraph>Domestic</Paragraph>
                  </div>
                ) : (
                  registrationState
                )}
                <HorizontalContainer>
                  <DocumentStatusIcon
                    enrichmentStatus={
                      enrichmentsMapping[
                        `${record['issuing-authority'] as UsSecretaryOfState}_${
                          record['file-number']
                        }`
                      ]?.['status']
                    }
                    registryRecord={record}
                  />
                  <IconContainer>
                    <StatusIcon status={record.status?.standardized ?? 'unknown'} />
                  </IconContainer>
                </HorizontalContainer>
              </StyledLi>
            );
          })}
      </ul>
    </SideNavFillSizeContainer>
  );
};

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Unknown', value: 'unknown' },
];
type StatusFilterOption = (typeof STATUS_FILTER_OPTIONS)[number]['value'];

const SORT_OPTIONS = [
  { label: 'Status', value: 'status' },
  { label: 'A to Z', value: 'a-to-z' },
  { label: 'Z to A', value: 'z-to-a' },
];
type SortOption = (typeof SORT_OPTIONS)[number]['value'];

const compareRegistryRecords = (
  a: UsSecretaryOfStateRecord,
  b: UsSecretaryOfStateRecord,
  sortBy: SortOption,
): number => {
  switch (sortBy) {
    case 'status':
      if (a['registration-type'] === 'Domestic') {
        return -1;
      } else if (b['registration-type'] === 'Domestic') {
        return 1;
      } else if (a.status?.standardized == null) {
        return 1;
      } else if (b.status?.standardized == null) {
        return 1;
      }
      return (
        a.status.standardized.localeCompare(b.status.standardized) ||
        compareRegistryRecords(a, b, 'a-to-z')
      );
    case 'a-to-z':
      if (a['registration-state'] == null) {
        return 1;
      } else if (b['registration-state'] == null) {
        return 1;
      }
      return a['registration-state'].localeCompare(b['registration-state']);
    case 'z-to-a':
      if (a['registration-state'] == null) {
        return 1;
      } else if (b['registration-state'] == null) {
        return 1;
      }
      return -a['registration-state'].localeCompare(b['registration-state']);
  }
  return -1;
};

const ShownRecordCount = ({
  searchedRegistryRecords,
  registryRecords,
}: {
  searchedRegistryRecords: UsSecretaryOfStateRecord[];
  registryRecords: UsSecretaryOfStateRecord[];
}) => {
  if (registryRecords.length === 0) return <Paragraph>No records found</Paragraph>;
  return (
    <Paragraph>{`${
      searchedRegistryRecords.length === registryRecords.length
        ? 'All'
        : searchedRegistryRecords.length + ' of'
    } ${registryRecords.length > 1 ? registryRecords.length : ''} filings shown`}</Paragraph>
  );
};

export const BusinessRegistrationsLookup = ({
  report,
  isRedacted,
}: {
  report: ReportBusinessRegistrationsLookup;
  isRedacted: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption[]>(
    STATUS_FILTER_OPTIONS.map((sfo) => sfo.value),
  );
  const [sortBy, setSortBy] = useState<SortOption>(SORT_OPTIONS[0].value);
  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });

  const [selectedView, setSelectedView] = useState<'all' | UsSecretaryOfStateRecord>('all');

  const {
    metadata: { 'database-business-token': brvToken },
    'business-name': businessName,
    'business-entity-type': businessEntityType,
    'business-formation-subdivision': businessFormationSubdivision,
    'business-formation-date': businessFormationDate,
    enrichments: registryRecordEnrichments,
    result: result,
  } = report.attributes;

  const registryRecords = result?.['registry-records'];

  const enrichmentsMapping = useMemo(() => {
    return registryRecordEnrichments.reduce(
      (acc, item) => {
        acc[`${item['issuing-authority']}_${item['file-number']}`] = item;
        return acc;
      },
      {} as Record<string, RegistryRecordEnrichment>,
    );
  }, [registryRecordEnrichments]);

  const formattedRegistryRecords = useMemo<UsSecretaryOfStateRecord[]>(
    () =>
      (registryRecords ?? []).map((record) => {
        return {
          ...record,
          'issue-date': formatDate(record['issue-date']),
          'registered-name': nameCase(record['registered-name']),
          'registration-type': nameCase(record['registration-type']),
          status: {
            standardized: record.status.standardized,
            raw: nameCase(record.status.raw),
          },
          'registration-state':
            record['registration-state'] &&
            US_STATES_AND_TERRITORIES[
              record['registration-state'] as keyof typeof US_STATES_AND_TERRITORIES
            ],
          'home-jurisdiction-state':
            record['home-jurisdiction-state'] &&
            US_STATES_AND_TERRITORIES[
              record['home-jurisdiction-state'] as keyof typeof US_STATES_AND_TERRITORIES
            ],
          'legal-entity-type': nameCase(record['legal-entity-type']),
          addresses: record.addresses
            ?.map((address) => {
              return {
                ...address,
                'street-1': nameCase(address['street-1']),
                'street-2': nameCase(address['street-2']),
                city: nameCase(address.city),
                type: formatAddressType(address.type?.replace(/_/g, ' ')),
              };
            })
            .sort((a, b) => a.type.localeCompare(b.type)),
          'associated-persons': record['associated-persons']
            ?.map((person) => {
              return {
                names: person?.names?.map((name) => {
                  return { value: nameCase(name.value) };
                }),
                positions: person?.positions?.map((position) => {
                  return { value: formatCompanyPosition(position.value) };
                }),
              };
            })
            .sort((a, b) => {
              const positionA = a?.positions?.at(0)?.value;
              const positionB = b?.positions?.at(0)?.value;
              if (positionA == null && positionB == null) return 0;
              if (positionA == null) return 1;
              if (positionB == null) return 1;
              return positionA.localeCompare(positionB);
            }),
          documents: record['documents']
            ?.map((doc) => {
              return {
                ...doc,
                date: DateTime.fromJSDate(new Date(doc.date)).toFormat('yyyy-MM-dd'),
              };
            })
            .sort((a, b) => (b.date > a.date ? 1 : -1)),
        } as UsSecretaryOfStateRecord;
      }),
    [registryRecords],
  );

  const filteredSortedRegistryRecords = useMemo<UsSecretaryOfStateRecord[]>(
    () =>
      formattedRegistryRecords
        .filter(
          (record) =>
            statusFilter.length === 0 ||
            statusFilter.includes(record.status?.standardized ?? 'unknown'),
        )
        .sort((a, b) => compareRegistryRecords(a, b, sortBy)),
    [formattedRegistryRecords, sortBy, statusFilter],
  );

  const registryRecordsFuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      findAllMatches: true,
      keys: [
        'registered-name',
        'registration-state',
        'file-number',
        ['status', 'raw'],
        ['addresses', 'type'],
        ['addresses', 'street-1'],
        ['addresses', 'city'],
        ['addresses', 'postal-code'],
        ['associated-persons', 'names', 'value'],
        ['associated-persons', 'positions', 'value'],
        ['documents', 'name'],
      ],
    };

    return new Fuse(filteredSortedRegistryRecords, fuseOptions);
  }, [filteredSortedRegistryRecords]);

  const searchedRegistryRecords = useMemo<UsSecretaryOfStateRecord[]>(
    () =>
      search === ''
        ? filteredSortedRegistryRecords
        : registryRecordsFuse.search(search).map(({ item }) => item),
    [filteredSortedRegistryRecords, search, registryRecordsFuse],
  );
  return (
    <Card>
      <Container>
        <SideNav>
          <SideNavFixedSizeContainer>
            <StyledParagraph size='lg' weight='bold' color='primary' as='h4'>
              Inputs
            </StyledParagraph>
            <StyledAttributeGrid>
              <Attribute
                label='Verified Business Record'
                value={<Link to={`/dashboard/verifications/${brvToken}`}>{brvToken}</Link>}
                redacted={isRedacted}
              />
            </StyledAttributeGrid>
          </SideNavFixedSizeContainer>
          <SideNavFixedSizeContainer>
            <StyledParagraph size='lg' weight='bold' color='primary' as='h4'>
              Business Details
            </StyledParagraph>
            <StyledAttributeGrid>
              <Attribute
                label='Business Name'
                value={nameCase(businessName)}
                redacted={isRedacted}
              />
              <Attribute
                label='Entity Type'
                value={nameCase(businessEntityType)}
                redacted={isRedacted}
              />
              <Attribute
                label='Formation State'
                value={
                  US_STATES_AND_TERRITORIES[
                    businessFormationSubdivision as keyof typeof US_STATES_AND_TERRITORIES
                  ]
                }
                redacted={isRedacted}
              />
              <Attribute
                label='Formation Date'
                value={formatDate(businessFormationDate)}
                redacted={isRedacted}
              />
            </StyledAttributeGrid>
          </SideNavFixedSizeContainer>
          <StatePicker
            records={formattedRegistryRecords ?? []}
            enrichmentsMapping={enrichmentsMapping}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
          />
        </SideNav>
        <MainContent>
          {selectedView === 'all' ? (
            <>
              <Heading size='sm' data-chromatic='ignore'>
                Secretary of State Filings
              </Heading>
              <ShownRecordCount
                searchedRegistryRecords={searchedRegistryRecords}
                registryRecords={registryRecords ?? []}
              />
              <SearchFilterBarContainer>
                <SearchInput
                  id='business-registrations__search'
                  title='Search'
                  handleInput={setSearch}
                  value={search}
                  placeholder='Search registrations'
                  size='md'
                />
                <div>
                  <FormControlLabel>Status</FormControlLabel>
                  <MultiSelect
                    value={statusFilter}
                    selectOptionValues={STATUS_FILTER_OPTIONS}
                    labelTextMapper={statusFilterLabelTextMapper}
                    onChange={setStatusFilter}
                    size='md'
                  />
                </div>
                <div>
                  <FormControlLabel>Sort</FormControlLabel>
                  <Select value={sortBy} onValueChange={setSortBy} size='md'>
                    {SORT_OPTIONS.map((option) => (
                      <Option key={`sort-option-select__${option.value}`} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </SearchFilterBarContainer>
              <RegistryRecordsContainer>
                {(searchedRegistryRecords ?? []).map((record) => (
                  <RegistryRecord
                    key={`registry-record__${record['issuing-authority']}-${record['file-number']}`}
                    registryRecord={record}
                    isRedacted={isRedacted}
                  />
                ))}
              </RegistryRecordsContainer>
            </>
          ) : (
            <RegistryRecord registryRecord={selectedView} isRedacted={false} />
          )}
        </MainContent>
      </Container>
    </Card>
  );
};

const formatDate = (dateString: string | undefined) => {
  if (dateString == null) {
    return null;
  }
  const date = new Date(dateString);
  return `${fd(dateString)} (${new Date().getFullYear() - date.getFullYear()} years ago)`;
};

const formatAddressType = (addressType: string | undefined) => {
  const formattedAddressType = nameCase(addressType) ?? 'Address';
  return formattedAddressType.includes('Address')
    ? formattedAddressType
    : `${formattedAddressType} Address`;
};
